import { Badge } from '@/components/ui/badge'

/**
 * Props for filter option renderers
 */
export interface OptionRendererProps {
  /** Value to send to backend */
  value: string
  /** Display label */
  label: string
}

/**
 * Format enum value to readable label
 * e.g., "ON_HOLD" -> "On Hold"
 */
export function formatEnumLabel(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Status field renderer - colored badges based on status value
 * ACTIVE = green, EXITED = red, others = default
 * Formats label: ACTIVE → "Active", ON_HOLD → "On Hold"
 */
function StatusRenderer({ value }: OptionRendererProps) {
  const variant =
    value === 'ACTIVE' ? 'green' : value === 'EXITED' ? 'red' : 'default'
  const formattedLabel = formatEnumLabel(value)
  return <Badge variant={variant}>{formattedLabel}</Badge>
}

/**
 * Funds field renderer - default badge with rounded corners
 * Shows label as-is (e.g., GSII stays GSII)
 */
function FundsRenderer({ label }: OptionRendererProps) {
  return <Badge className="rounded-sm">{label}</Badge>
}

/**
 * Default renderer for all other fields
 * Formats label: ACTIVE → "Active", ON_HOLD → "On Hold"
 */
function DefaultRenderer({ value }: OptionRendererProps) {
  const formattedLabel = formatEnumLabel(value)
  return <Badge>{formattedLabel}</Badge>
}

/**
 * Registry mapping fieldName to renderer component
 */
export const FILTER_OPTION_RENDERERS: Record<
  string,
  React.ComponentType<OptionRendererProps>
> = {
  status: StatusRenderer,
  funds: FundsRenderer,
}

/**
 * Get the appropriate renderer for a field
 * Falls back to DefaultRenderer if no specific renderer is registered
 */
export function getOptionRenderer(
  fieldName: string
): React.ComponentType<OptionRendererProps> {
  return FILTER_OPTION_RENDERERS[fieldName] || DefaultRenderer
}
